import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, PaletteMode, useMediaQuery, CssBaseline } from '@mui/material';
import { useAuth } from './AuthContext';
import { themeStorage } from '../utils/themeStorage';

type ThemePreference = 'light' | 'dark' | 'system';

type ThemeModeContextType = {
  preference: ThemePreference;
  resolvedMode: Exclude<ThemePreference, 'system'>;
  setPreference: (pref: ThemePreference) => void;
  muiTheme: ReturnType<typeof createTheme>;
};

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

// Brand color tokens
const brand = {
  primary: { main: '#0a7ca0', light: '#4aafce', dark: '#005173', contrastText: '#ffffff' },
  secondary: { main: '#00b7c7', light: '#62e9f9', dark: '#008796', contrastText: '#ffffff' },
};

function createAppTheme(mode: PaletteMode) {
  // Base tokens that are shared
  const base = {
    typography: {
      fontFamily: '"Sora", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 500 },
      h2: { fontSize: '2rem', fontWeight: 500 },
      h3: { fontSize: '1.75rem', fontWeight: 500 },
      h4: { fontSize: '1.5rem', fontWeight: 500 },
      body1: { fontSize: '1rem' },
      button: { textTransform: 'none' },
    },
    spacing: 8,
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, minHeight: 44, minWidth: 44 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { minHeight: 44, minWidth: 44 },
          sizeSmall: { minHeight: 44, minWidth: 44, fontSize: '1.2rem' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
        },
      },
    },
  } as const;

  if (mode === 'light') {
    return createTheme({
      palette: {
        mode,
        primary: brand.primary,
        secondary: brand.secondary,
        background: { default: '#f5f8fa', paper: '#ffffff' },
        text: { primary: '#212121', secondary: '#546e7a' },
      },
      ...base,
    });
  }

  // Dark mode
  return createTheme({
    ...base,
    palette: {
      mode,
      primary: brand.primary,
      secondary: brand.secondary,
      background: { default: '#0c1116', paper: '#0f1720' },
      text: { primary: '#e6edf3', secondary: '#9fb2c0' },
      divider: 'rgba(255,255,255,0.12)',
      action: { hover: 'rgba(255,255,255,0.06)' },
    },
    components: {
      ...base.components,
      MuiCssBaseline: {
        styleOverrides: {
          '::selection': {
            backgroundColor: '#0a7ca0',
            color: '#ffffff',
          },
          '*::-webkit-scrollbar': {
            width: 10,
            height: 10,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.06)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          },
        },
      },
    },
  });
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [preference, setPreferenceState] = useState<ThemePreference>('system');

  // Load saved preference per user and perform migration if needed
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Attempt migration from encrypted storage (only runs once)
        await themeStorage.migrateFromEncryptedStorage();
        
        const userId = user?.id || 'anonymous';
        const saved = themeStorage.getUserPreference(userId);
        if (mounted && saved) {
          setPreferenceState(saved);
        }
      } catch (error) {
        console.warn('Failed to load theme preference:', error);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const setPreference = async (pref: ThemePreference) => {
    setPreferenceState(pref);
    try {
      const userId = user?.id || 'anonymous';
      themeStorage.setUserPreference(userId, pref);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
      // Storage failure is non-critical - the UI will still update
    }
  };

  const resolvedMode: Exclude<ThemePreference, 'system'> = useMemo(() => {
    if (preference === 'system') {
      return prefersDark ? 'dark' : 'light';
    }
    return preference;
  }, [preference, prefersDark]);

  const muiTheme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const value = useMemo(
    () => ({ preference, resolvedMode, setPreference, muiTheme }),
    [preference, resolvedMode, muiTheme]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
};
